import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useState } from "react"

import ImamStorePreview from "@/assets/hero-imamabubakarbrand.png"
import BookOnTheSpotPreview from "@/assets/hero-bookonthespot.png"
import GrandHillsPreview from "@/assets/hero-grandhills.png"
import { cn } from "@/lib/utils"

import { easeOut } from "./marketing-motion"
import { SalesLinkClaim } from "./SalesLinkClaim"

const slides = [
  {
    handle: "imamabubakarbrand",
    src: ImamStorePreview,
    alt: "Imam Abubakar Brand storefront preview",
  },
  {
    handle: "bookonthespot",
    src: BookOnTheSpotPreview,
    alt: "Book On The Spot storefront preview",
  },
  {
    handle: "grandhills",
    src: GrandHillsPreview,
    alt: "Grand Hills storefront preview",
  },
] as const

const SLIDE_MS = 4000

export function HeroShowcase() {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const slide = slides[index]

  useEffect(() => {
    if (paused) return
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length)
    }, SLIDE_MS)
    return () => window.clearInterval(id)
  }, [paused])

  return (
    <section className="relative overflow-hidden bg-[#be9afc] px-4 pt-36 pb-16 sm:px-6 sm:pt-40 sm:pb-20 lg:pb-24 xl:pt-48">
      <div
        className="pointer-events-none absolute -right-24 top-0 size-96 rounded-full bg-secondary/25 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-32 bottom-0 size-80 rounded-full bg-white/5 blur-3xl"
        aria-hidden
      />

      <div className="relative mx-auto max-w-[96%] lg:max-w-[80%]">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-8">
          <div className="text-center lg:text-left">
            <motion.h1
              className="font-display mt-6 text-5xl leading-[0.9] text-primary lg:text-[3.5rem] xl:text-[4.5rem]"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.5, ease: easeOut }}
            >
              One link for everything you sell
            </motion.h1>

            <motion.p
              className="mx-auto mt-5 text-base font-semibold leading-relaxed text-primary xl:text-lg lg:mx-0"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5, ease: easeOut }}
            >
              Share sqale.shop/yourstore on Instagram, WhatsApp, and everywhere
              you sell. Customers browse, buy, and track orders without the endless DMs.
            </motion.p>

            <motion.div
              className="mt-8 flex justify-center lg:justify-start"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.14, duration: 0.45, ease: easeOut }}
            >
              <SalesLinkClaim onDark className="max-w-md lg:max-w-lg" />
            </motion.div>
          </div>

          <div className="relative flex justify-center lg:justify-end">
            <div
              className="relative w-full max-w-[300px] sm:max-w-[320px]"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              <div className="absolute bottom-6 left-6 z-10 hidden overflow-hidden rounded-2xl border border-white/20 bg-primary p-3 shadow-lg backdrop-blur-sm sm:block lg:-right-4">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={slide.handle}
                    className="text-xs font-semibold text-primary-foreground"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.28, ease: easeOut }}
                  >
                    sqale.shop/{slide.handle}
                  </motion.p>
                </AnimatePresence>
              </div>

              <motion.div
                className="overflow-hidden rounded-[2rem] border-4 border-white/20 bg-card shadow-[0_32px_80px_-24px_rgba(0,0,0,0.45)] ring-1 ring-white/10"
                initial={{ opacity: 0, y: 24, rotate: 1 }}
                animate={{ opacity: 1, y: 0, rotate: 0 }}
                transition={{ delay: 0.2, duration: 0.6, ease: easeOut }}
              >
                <motion.div
                  className="flex"
                  animate={{ x: `-${index * 100}%` }}
                  transition={{ duration: 0.55, ease: easeOut }}
                >
                  {slides.map((item) => (
                    <img
                      key={item.handle}
                      src={item.src}
                      alt={item.alt}
                      className="h-[min(620px,70vh)] w-full shrink-0 bg-white object-cover object-top"
                    />
                  ))}
                </motion.div>
              </motion.div>

            
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
